/**
 * Smart Shikka AI Core — PR100
 * Smart Print Readonly Drive Access Registry — Readonly Only / User Selected Folder
 *
 * Approved readonly access to user-selected Smart Print folder metadata only.
 * No write, delete, import, export, backup, restore, or hidden scanning.
 */
function deepFreeze(obj) {
    const propNames = Object.getOwnPropertyNames(obj);
    for (const name of propNames) {
        const value = obj[name];
        if (value && typeof value === "object" && !Object.isFrozen(value)) {
            deepFreeze(value);
        }
    }
    return Object.freeze(obj);
}
const BLOCKED_ACTIONS = [
    "auto_delete",
    "auto_save",
    "backup_execute",
    "child_process",
    "export_file",
    "file_write",
    "import_file",
    "old_assistant_alias",
    "restore_execute",
    "secret_literal",
    "shell_command",
    "unapproved_db_write",
    "unapproved_file_write",
    "unapproved_network_call",
    "unknown_provider_call",
    "unknown_tool_execution",
    "unredacted_secret",
    "unsafe_env_dump",
    "hidden_scan",
    "drive_write",
    "unscoped_folder_access",
    "unapproved_drive_access",
    "runtime_unlock",
    "broad_runtime_unlock",
    "network_call",
    "provider_call",
    "role_mutation",
    "permission_mutation",
];
const COMMON_SAFETY = {
    previewOnly: true,
    readonlyOnly: true,
    requiresOwnerApproval: true,
    requiresUserApproval: true,
    requiresUserSelectedFolder: true,
    fileWriteAllowedInPr100: false,
    fileDeleteAllowedInPr100: false,
    importAllowedInPr100: false,
    exportAllowedInPr100: false,
    backupAllowedInPr100: false,
    restoreAllowedInPr100: false,
    hiddenScanAllowedInPr100: false,
    driveWriteAllowedInPr100: false,
    secretLiteralAllowedInPr100: false,
    secretReadAllowedInPr100: false,
    shellCommandAllowedInPr100: false,
    childProcessAllowedInPr100: false,
    networkCallAllowedInPr100: false,
    providerCallAllowedInPr100: false,
    dbWriteAllowedInPr100: false,
    unscopedFolderAccessAllowedInPr100: false,
    unapprovedAccessAllowedInPr100: false,
    runtimeUnlockAllowedInPr100: false,
    broadRuntimeUnlockAllowedInPr100: false,
    roleMutationAllowedInPr100: false,
    permissionMutationAllowedInPr100: false,
};
const registry = [
    {
        id: "smart_print_readonly_drive_access_summary",
        displayName: "Smart Print Readonly Drive Access Summary",
        description: "Summary of Smart Print local drive access scope. Readonly access to user-selected Smart Print folder metadata only — no write, delete, import, export, backup, restore, or hidden scanning.",
        sourceModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        targetPreviewModules: ["smartShikkaAssistantSafeInternalToolExecutionRegistry"],
        relatedRouteIds: ["smart_print_readonly_access_request"],
        relatedUiPreviewIds: ["smart_print_readonly_access_route_card"],
        relatedChatPreviewIds: ["smart_print_readonly_access_result_card"],
        relatedConversationStatePreviewIds: ["readonly_smart_print_access_state"],
        relatedApprovalStatePreviewIds: ["readonly_smart_print_access_approval_state"],
        relatedActionRequestPreviewIds: ["readonly_smart_print_access_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Readonly metadata access only", "User-selected folder required", "No write, delete, import, export, backup, restore, or hidden scan"],
        futurePrScope: ["Future Smart Print write gate with approval", "Smart Print sync planner"],
        notes: ["Approved readonly access to user-selected Smart Print folder metadata only", "Hard readonly boundary", "No side effects allowed"],
    },
    {
        id: "user_selected_folder_contract",
        displayName: "User Selected Folder Contract",
        description: "Contract requiring explicit user-selected folder metadata before any Smart Print readonly access. No implicit, default, or hidden folder access.",
        sourceModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyApprovalStatePreviewRegistry"],
        relatedRouteIds: ["smart_print_folder_selection_request"],
        relatedUiPreviewIds: ["smart_print_folder_selection_route_card"],
        relatedChatPreviewIds: ["smart_print_folder_selection_result_card"],
        relatedConversationStatePreviewIds: ["readonly_user_selected_folder_state"],
        relatedApprovalStatePreviewIds: ["readonly_smart_print_access_approval_state"],
        relatedActionRequestPreviewIds: ["readonly_user_selected_folder_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["No user-selected folder provided", "Folder scope not explicitly approved", "Implicit folder access blocked"],
        futurePrScope: ["Multi-folder selection contract", "Folder scope revocation flow"],
        notes: ["Folder must be explicitly selected by the user", "No default or hidden folder access", "Folder scope is immutable per session"],
    },
    {
        id: "smart_print_company_metadata_readonly",
        displayName: "Smart Print Company Metadata Readonly",
        description: "Readonly contract for Smart Print company metadata inside the user-selected folder. Read-only — no mutation of company configuration.",
        sourceModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyConversationStatePreviewRegistry"],
        relatedRouteIds: ["smart_print_company_metadata_request"],
        relatedUiPreviewIds: ["smart_print_company_metadata_route_card"],
        relatedChatPreviewIds: ["smart_print_company_metadata_result_card"],
        relatedConversationStatePreviewIds: ["readonly_smart_print_company_metadata_state"],
        relatedApprovalStatePreviewIds: ["readonly_smart_print_access_approval_state"],
        relatedActionRequestPreviewIds: ["readonly_smart_print_company_metadata_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Company metadata is read-only", "No company configuration mutation", "Access restricted to user-selected folder"],
        futurePrScope: ["Company metadata diff preview", "Company metadata change request gate"],
        notes: ["Reads company metadata only", "No write to company configuration", "Scoped to user-selected folder"],
    },
    {
        id: "smart_print_file_inventory_readonly",
        displayName: "Smart Print File Inventory Readonly",
        description: "Readonly contract for listing Smart Print file inventory metadata (names, sizes, timestamps) inside the user-selected folder. No file content mutation.",
        sourceModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyActionRequestPreviewRegistry"],
        relatedRouteIds: ["smart_print_file_inventory_request"],
        relatedUiPreviewIds: ["smart_print_file_inventory_route_card"],
        relatedChatPreviewIds: ["smart_print_file_inventory_result_card"],
        relatedConversationStatePreviewIds: ["readonly_smart_print_file_inventory_state"],
        relatedApprovalStatePreviewIds: ["readonly_smart_print_access_approval_state"],
        relatedActionRequestPreviewIds: ["readonly_smart_print_file_inventory_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["File inventory is read-only metadata", "No file write, delete, import, or export", "Access restricted to user-selected folder"],
        futurePrScope: ["File content readonly preview", "File inventory change watcher"],
        notes: ["Lists file metadata only", "No file content mutation", "Scoped to user-selected folder"],
    },
    {
        id: "smart_print_readonly_access_approval_gate",
        displayName: "Smart Print Readonly Access Approval Gate",
        description: "Gate requiring dual approval (owner and user) plus explicit user-selected folder before any Smart Print readonly access. No bypass.",
        sourceModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        targetPreviewModules: ["smartShikkaAssistantApprovalRuntimeGateRegistry"],
        relatedRouteIds: ["smart_print_readonly_access_request"],
        relatedUiPreviewIds: ["smart_print_readonly_access_route_card"],
        relatedChatPreviewIds: ["smart_print_access_blocked_card"],
        relatedConversationStatePreviewIds: ["readonly_smart_print_access_state"],
        relatedApprovalStatePreviewIds: ["readonly_smart_print_dual_approval_required_state"],
        relatedActionRequestPreviewIds: ["readonly_smart_print_access_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Owner approval missing", "User approval missing", "User-selected folder missing — approval gate not satisfied"],
        futurePrScope: ["Scoped re-approval flow", "Approval delegation with audit"],
        notes: ["Both owner and user must approve", "User-selected folder required", "No approval bypass"],
    },
    {
        id: "smart_print_drive_scope_boundary",
        displayName: "Smart Print Drive Scope Boundary",
        description: "Hard boundary restricting all access to the approved user-selected Smart Print folder. No traversal outside the selected folder scope.",
        sourceModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyExecutionGateMatrixPreviewRegistry"],
        relatedRouteIds: ["smart_print_readonly_access_request"],
        relatedUiPreviewIds: ["smart_print_readonly_access_route_card"],
        relatedChatPreviewIds: ["smart_print_access_blocked_card"],
        relatedConversationStatePreviewIds: ["readonly_smart_print_access_state"],
        relatedApprovalStatePreviewIds: ["readonly_smart_print_access_approval_state"],
        relatedActionRequestPreviewIds: ["readonly_smart_print_access_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Path outside user-selected folder scope", "No directory traversal beyond selected folder", "Unscoped folder access blocked"],
        futurePrScope: ["Nested folder approval", "Per-subfolder scope grants"],
        notes: ["Access confined to the user-selected folder", "No traversal outside scope", "Boundary enforced at contract level"],
    },
    {
        id: "smart_print_no_write_boundary",
        displayName: "Smart Print No Write Boundary",
        description: "Hard boundary blocking all write, delete, import, export, backup, and restore operations on Smart Print data. Readonly only.",
        sourceModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        targetPreviewModules: ["smartShikkaAssistantSafeInternalToolExecutionRegistry"],
        relatedRouteIds: ["smart_print_readonly_access_request"],
        relatedUiPreviewIds: ["smart_print_readonly_access_route_card"],
        relatedChatPreviewIds: ["smart_print_write_blocked_card"],
        relatedConversationStatePreviewIds: ["readonly_smart_print_access_state"],
        relatedApprovalStatePreviewIds: ["readonly_smart_print_access_approval_state"],
        relatedActionRequestPreviewIds: ["readonly_smart_print_write_blocked_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Write operations blocked — readonly only", "No delete, import, export, backup, or restore", "No file mutation allowed"],
        futurePrScope: ["Future Smart Print write gate with dual approval", "Staged write simulation"],
        notes: ["Readonly hard boundary", "No write, delete, import, export, backup, or restore", "Write capability deferred to a future gated PR"],
    },
    {
        id: "smart_print_no_hidden_scan_boundary",
        displayName: "Smart Print No Hidden Scan Boundary",
        description: "Hard boundary blocking any hidden, background, or implicit scanning of the drive. All access must be explicit and within the user-selected folder.",
        sourceModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        targetPreviewModules: ["smartShikkaAssistantNoRuntimeSafetyAuditPackRegistry"],
        relatedRouteIds: ["smart_print_readonly_access_request"],
        relatedUiPreviewIds: ["smart_print_readonly_access_route_card"],
        relatedChatPreviewIds: ["smart_print_access_blocked_card"],
        relatedConversationStatePreviewIds: ["readonly_smart_print_access_state"],
        relatedApprovalStatePreviewIds: ["readonly_smart_print_access_approval_state"],
        relatedActionRequestPreviewIds: ["readonly_smart_print_access_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Hidden scanning blocked", "No background or implicit drive scan", "All access must be explicit and user-approved"],
        futurePrScope: ["Explicit user-triggered rescan with approval", "Scan audit ledger"],
        notes: ["No hidden, background, or implicit scanning", "Every read is explicit and scoped", "No automatic full-drive crawl"],
    },
    {
        id: "smart_print_readonly_audit_evidence",
        displayName: "Smart Print Readonly Audit Evidence",
        description: "Audit evidence contract for all Smart Print readonly access. Every readonly access produces immutable, redacted audit evidence.",
        sourceModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        targetPreviewModules: ["smartShikkaAssistantApprovalRuntimeGateRegistry"],
        relatedRouteIds: ["smart_print_readonly_audit_request"],
        relatedUiPreviewIds: ["smart_print_readonly_audit_route_card"],
        relatedChatPreviewIds: ["smart_print_readonly_audit_transcript_card"],
        relatedConversationStatePreviewIds: ["readonly_smart_print_audit_transcript_state"],
        relatedApprovalStatePreviewIds: ["readonly_smart_print_audit_evidence_state"],
        relatedActionRequestPreviewIds: ["readonly_smart_print_audit_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Audit evidence incomplete", "No access without audit", "Raw paths must be redacted"],
        futurePrScope: ["Audit export with approval", "Audit retention policy"],
        notes: ["Every readonly access generates audit evidence", "Audit is append-only and immutable", "Sensitive paths are redacted"],
    },
    {
        id: "future_smart_print_write_gate_ready",
        displayName: "Future Smart Print Write Gate Ready",
        description: "Placeholder readiness marker for a future Smart Print write gate. Not active in PR100 — no write capability exists yet.",
        sourceModules: ["smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry"],
        targetPreviewModules: ["smartShikkaAssistantReadonlyExecutionGateMatrixPreviewRegistry"],
        relatedRouteIds: ["smart_print_readonly_access_request"],
        relatedUiPreviewIds: ["smart_print_readonly_access_route_card"],
        relatedChatPreviewIds: ["smart_print_write_blocked_card"],
        relatedConversationStatePreviewIds: ["readonly_smart_print_access_state"],
        relatedApprovalStatePreviewIds: ["readonly_smart_print_access_approval_state"],
        relatedActionRequestPreviewIds: ["readonly_smart_print_write_blocked_request"],
        ...COMMON_SAFETY,
        blockedActions: [...BLOCKED_ACTIONS],
        denialReasons: ["Smart Print write gate not yet available", "Requires future gated PR", "All write access blocked in PR100"],
        futurePrScope: ["Smart Print write gate with dual approval", "Smart Print save simulation", "Smart Print restore with approval"],
        notes: ["This entry is a future readiness placeholder", "No write capability exists in PR100", "Activation requires a separate gated PR"],
    },
];
deepFreeze(registry);
export function getSmartShikkaAssistantSmartPrintReadonlyDriveAccessEntries() {
    return registry;
}
export function getSmartShikkaAssistantSmartPrintReadonlyDriveAccessEntryById(id) {
    return registry.find((e) => e.id === id);
}
//# sourceMappingURL=smartShikkaAssistantSmartPrintReadonlyDriveAccessRegistry.js.map